<template>
  <div v-if="invoice">
    <app-header>
      <template #title>
        <h1 class="d-inline me-4">Invoice #{{ invoice.xero_number }}</h1>
      </template>
      <template v-slot:actions>
        <span
          class="status ms-2"
          :class="{
            'status-approved': invoice.status == 'PAID',
            'status-draft': invoice.status == 'DRAFT',
            'status-sent': invoice.status == 'AUTHORISED',
            'status-declined': invoice.status == 'DELETED',
          }"
        >{{ invoice.status }}</span>
      </template>
    </app-header>

    <div class="container my-5">
      <div class="row display-flex">
        <div class="col-lg-5 col-xl-4">
          <div class="card">
            <div class="card-body">
              <h2 class="card-title mb-3">Customer</h2>
              <div class="row" v-if="invoice.client">
                <div class="col-12">
                  <p>
                    <span class="data-label">Company</span>
                    {{ invoice.client.company }}
                  </p>
                </div>
                <div class="col-12">
                  <p>
                    <span class="data-label">Email</span>
                    {{ invoice.client.email }}
                  </p>
                </div>
                <div class="col-lg-6">
                  <p>
                    <span class="data-label">First Name</span>
                    {{ invoice.client.firstname }}
                  </p>
                </div>
                <div class="col-lg-6">
                  <p>
                    <span class="data-label">Last Name</span>
                    {{ invoice.client.surname }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-7 col-xl-8">
          <div class="card">
            <div class="card-body">
              <div class="row mb-3">
                <div class="col d-flex align-items-center">
                  <h2>Invoice Details</h2>
                </div>
                <div class="col-auto">
                  <button
                    type="button"
                    class="btn btn-sm btn-link"
                    v-c-modal:invoiceEditDrawer="{}"
                  >
                    <i class="far fa-pencil-alt"></i>
                    Edit
                  </button>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-6">
                  <p>
                    <span class="data-label">Raised</span>
                    {{ invoice.raised | moment("DD/MM/YYYY") }}
                  </p>
                </div>
                <div class="col-lg-6">
                  <p>
                    <span class="data-label">Due</span>
                    {{ invoice.due | moment("DD/MM/YYYY") }}
                  </p>
                </div>
                <div class="col-lg-6">
                  <p>
                    <span class="data-label">Reference</span>
                    {{ invoice.reference || '-' }}
                  </p>
                </div>
                <div class="col-lg-6">
                  <p>
                    <span class="data-label">Xero Number</span>
                    {{ invoice.xero_number || '-' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-12">
          <div class="card">
            <div class="card-body p-0">
              <h2 class="mb-3 px-3 pt-3">Line Items</h2>
              <table class="table line-items-table mb-0">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style="width: 120px;">Account</th>
                    <th class="text-end" style="width: 80px;">Qty</th>
                    <th class="text-end" style="width: 120px;">Unit Price</th>
                    <th class="text-end" style="width: 120px;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in invoice.lines" :key="line.id">
                    <td><pre class="mb-0" style="white-space: pre-wrap; font-family: inherit;">{{ line.description }}</pre></td>
                    <td class="text-muted">{{ line.account_code }}</td>
                    <td class="text-end">{{ line.quantity }}</td>
                    <td class="text-end">${{ line.unit_amount | currency }}</td>
                    <td class="text-end">${{ line.amount | currency }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="4" class="text-end border-0">Subtotal</td>
                    <td class="text-end border-0">${{ invoice.subtotal | currency }}</td>
                  </tr>
                  <tr>
                    <td colspan="4" class="text-end border-0">GST</td>
                    <td class="text-end border-0">${{ invoice.gst | currency }}</td>
                  </tr>
                  <tr class="strong">
                    <td colspan="4" class="text-end border-0">Total including GST</td>
                    <td class="text-end border-0">${{ invoice.total | currency }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <edit-drawer ref="invoiceEditDrawer" :invoice="invoice"></edit-drawer>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import EditDrawer from "./EditDrawer";

export default {
  components: { EditDrawer },

  computed: {
    ...mapState("invoice", {
      invoice: "single",
    }),
  },

  created: function () {
    this.getInvoice(this.$route.params.invoice_id);
  },

  methods: {
    ...mapActions("invoice", { getInvoice: "getSingle" }),
  },
};
</script>

<style scoped>
.line-items-table thead th,
.line-items-table tbody td,
.line-items-table tfoot td {
  background-color: transparent;
}

.line-items-table thead th {
  border-bottom-width: 1px;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}

.line-items-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.line-items-table tbody td {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  vertical-align: top;
}

.line-items-table tfoot td {
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.line-items-table tfoot tr.strong td {
  color: #111;
  font-size: 1rem;
  padding-top: 0.6rem;
}
</style>